import React from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const GuideDownloader = ({ buttonText = "Download FREE Guide Now", buttonStyle = {} }) => {
  const downloadGuide = async () => {
    try {
      // Create a temporary div to hold the guide content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; background-color: #f9f9f9;">
          <div style="background-color: white; margin: 20px auto; padding: 40px; max-width: 800px; box-shadow: 0 0 10px rgba(0,0,0,0.1);">
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="/RapidMind2025.png" alt="RapidMind Logo" style="max-width: 200px;">
            </div>
            <h1 style="color: #007bff; text-align: center; margin-bottom: 10px;">Welcome to RapidMind</h1>
            <p style="text-align: center;"><strong>Empowering freelancers with AI tools to save time and win more projects.</strong></p>
            <p>RapidMind is your ultimate platform for freelancers seeking to streamline their workflow with cutting-edge AI-powered tools. Whether you're optimizing your online presence, enhancing images, or analyzing performance, our tools help you stand out and succeed in a competitive market.</p>
            <p>We solve common freelancer challenges like time-consuming SEO tasks, image quality issues, and lack of data-driven insights, allowing you to focus on what you do best: delivering exceptional work.</p>
          </div>

          <div style="background-color: white; margin: 20px auto; padding: 40px; max-width: 800px; box-shadow: 0 0 10px rgba(0,0,0,0.1); page-break-before: always;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 5px;">How to Get Started</h2>
            <p>Getting started with RapidMind is simple and quick. Follow these steps to unlock the full potential of our platform:</p>
            <div style="margin-bottom: 15px;">
              <strong style="color: #007bff;">1. Sign Up / Log In:</strong> Create a free account or log in with your existing credentials. Choose your role (Freelancer, Entrepreneur, etc.) to personalize your experience.
            </div>
            <div style="margin-bottom: 15px;">
              <strong style="color: #007bff;">2. Create Your Profile:</strong> Build a professional profile showcasing your skills, portfolio, and achievements. Upload your avatar and add details to increase visibility.
            </div>
            <div style="margin-bottom: 15px;">
              <strong style="color: #007bff;">3. Access Tools:</strong> Navigate to the dashboard and explore our AI tools. Start with free trials to see how they can boost your productivity.
            </div>
            <div style="margin-bottom: 15px;">
              <strong style="color: #007bff;">4. Publish & Share:</strong> Use your optimized content and insights to create compelling proposals or portfolios. Share your profile on social media to attract more clients.
            </div>
          </div>

          <div style="background-color: white; margin: 20px auto; padding: 40px; max-width: 800px; box-shadow: 0 0 10px rgba(0,0,0,0.1); page-break-before: always;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 5px;">Features & Tools</h2>
            <p>Discover the powerful tools designed to elevate your freelancing career:</p>
            <ul style="list-style-type: disc; margin-left: 20px;">
              <li><strong>AI-Powered SEO Checker:</strong> Analyze and optimize your website or content for better search engine rankings. Save time on manual SEO audits and improve your online visibility.</li>
              <li><strong>Image Optimization Tool:</strong> Enhance and compress images without losing quality. Perfect for portfolios, proposals, and social media, ensuring faster load times and professional presentation.</li>
              <li><strong>Analytics Dashboard:</strong> Track your performance with detailed insights on usage, earnings, and client interactions. Make data-driven decisions to grow your business.</li>
            </ul>
            <p>Each tool is built with AI to automate tedious tasks, giving you more time to focus on creative and strategic work.</p>
          </div>

          <div style="background-color: white; margin: 20px auto; padding: 40px; max-width: 800px; box-shadow: 0 0 10px rgba(0,0,0,0.1); page-break-before: always;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 5px;">Tips to Boost Your Appearance</h2>
            <p>Your "appearance rate" on RapidMind measures how visible and attractive your profile is to potential clients. A higher rate means more opportunities. Here's how to improve it:</p>
            <ul style="list-style-type: disc; margin-left: 20px;">
              <li><strong>Complete Your Profile:</strong> Fill in all sections, including skills, experience, and portfolio. This can add up to +30% to your visibility.</li>
              <li><strong>Use Tools Daily:</strong> Regularly utilize our AI tools to generate optimized content and insights, demonstrating active engagement.</li>
              <li><strong>Share Your Profile:</strong> Promote your RapidMind profile on LinkedIn, Twitter (X), and other platforms to expand your reach.</li>
              <li><strong>Earn Badges & Referrals:</strong> Participate in challenges and refer friends to unlock badges that further boost your appearance.</li>
            </ul>
            <p>Monitor your appearance rate in the dashboard and aim for continuous improvement!</p>
          </div>

          <div style="background-color: white; margin: 20px auto; padding: 40px; max-width: 800px; box-shadow: 0 0 10px rgba(0,0,0,0.1); page-break-before: always;">
            <h2 style="color: #007bff; border-bottom: 2px solid #007bff; padding-bottom: 5px;">Support & Contact</h2>
            <p>We're here to help you succeed. If you have questions or need assistance:</p>
            <ul style="list-style-type: disc; margin-left: 20px;">
              <li><strong>FAQ:</strong> Visit our FAQ page for quick answers to common questions.</li>
              <li><strong>Contact Form:</strong> Reach out via our contact form for personalized support.</li>
              <li><strong>Email:</strong> Send us an email at support@rapidmind.com.</li>
              <li><strong>Social Channels:</strong> Follow us on LinkedIn, Twitter, and Facebook for updates and tips.</li>
            </ul>
            <p>We grow better with your ideas! Share your feedback to help us improve RapidMind.</p>
          </div>
        </div>
      `;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);

      // Use html2canvas to capture the content
      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f9f9f9'
      });

      // Remove the temporary div
      document.body.removeChild(tempDiv);

      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Download the PDF
      pdf.save('RapidMind_Visitor_Guide.pdf');

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the guide. Please try again.');
    }
  };

  return (
    <button
      onClick={downloadGuide}
      style={{
        background: '#3b82f6',
        color: '#fff',
        border: 'none',
        padding: '15px 30px',
        fontSize: '1.1rem',
        borderRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold',
        ...buttonStyle
      }}
    >
      {buttonText}
    </button>
  );
};

export default GuideDownloader;
